import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type {
  ImmutableEntity,
  ImmutableRootState,
  NodeEntity,
  ReferenceField,
  TaxonomyEntity,
} from '@uncinc/react-drupal-core/types';

import {
  Accordion,
  AccordionItem,
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper } from '../../../components';
import { DEFAULT_LANGUAGE } from '../../../config/constants';
import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { NodeFAQ } from '../../../types/uncinc_profile/nodes';
import type { ParagraphFAQ as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface ParagraphFAQAccordionItemProps {
  faqItem: ReferenceField<NodeFAQ>;
}

const ParagraphFAQAccordionItem: React.FC<ParagraphFAQAccordionItemProps> = ({ faqItem }) => {
  const faqKey = faqItem.target_id;
  const faqQuestion = faqItem.entity.title[0].value;
  const faqAnswer = faqItem.entity.body[0].processed;
  const faqMedia = faqItem.entity.field_media_item[0];
  const faqMediaCaption = faqMedia?.entity.field_caption[0]?.value;
  const faqLink = faqItem.entity.field_link[0];
  const faqLinkTitle = faqLink?.title;
  const faqLinkUrl = faqLink?.alias || faqLink?.uri;

  return (
    <AccordionItem
      key={faqKey}
      target_id={faqKey}
      title={faqQuestion}
      className="ParagraphFAQ__accordion__item"
      materialIconName="keyboard_arrow_up"
      iconClassName="toggleIcon"
      content={(
        <>
          <WysiwygField
            className="ParagraphFAQ__accordion__item__answer"
            html={faqAnswer}
          />

          {(faqLink || faqMedia) && (
            <div className="ParagraphFAQ__accordion__item__wrapper">
              {faqLinkUrl && (
                <ButtonWrapper
                  type="textual"
                  href={faqLinkUrl}
                >
                  {faqLinkTitle}
                </ButtonWrapper>
              )}

              {faqMedia && (
                <DynamicMediaItem
                  media={faqMedia}
                  imageAspectRatio="4_3"
                  className="ParagraphFAQ__accordion__item__media"
                  caption={faqMediaCaption}
                />
              )}
            </div>
          )}
        </>
      )}
    />
  );
};

interface ParagraphFAQProps {
  id: string;
  entity: ImmutableEntity<Type>;
}

const ParagraphFAQ: React.FC<ParagraphFAQProps> = ({ id, entity }) => {
  const {
    textColClasses,
    textColClassesHorizontal,
    faqColClasses,
    faqColClassesHorizontal,
  } = useLayoutVariables('paragraphFAQ');

  const paragraphFAQ = entity.toJS();
  const title = paragraphFAQ.field_title[0]?.value;
  const body = paragraphFAQ.field_body[0]?.processed;
  const faqItems = paragraphFAQ.field_faq;

  const path = useSelector<ImmutableRootState<unknown>, string>((state) => {
    const currentEntityId = state.getIn(['entity', 'entityId'], null) as string | null;
    const currentEntityType = state.getIn(['entity', 'entityType'], null) as string | null;
    const currentEntity = state.getIn(
      [currentEntityType === 'taxonomy_term' ? 'taxonomyTerm' : currentEntityType, currentEntityId],
    ) as ImmutableEntity<NodeEntity | TaxonomyEntity>;
    const langcode = currentEntity.getIn(['langcode', 0, 'value'], DEFAULT_LANGUAGE);

    return currentEntity.getIn(['paths', langcode], '/');
  });

  const schema = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': path,
    mainEntity: faqItems.map((faqItem) => ({
      '@type': 'Question',
      name: faqItem.entity.title[0].value,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.entity.body[0].processed,
      },
    })),
  }), [faqItems, path]);

  const horizontal = paragraphFAQ.field_horizontal?.[0]?.value;
  const textClasses = classNames({
    [textColClasses]: horizontal === false,
    [textColClassesHorizontal]: horizontal,
  });
  const faqClasses = classNames({
    [faqColClasses]: horizontal === false,
    [faqColClassesHorizontal]: horizontal,
  });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>

      <div id={id} className="Paragraph ParagraphFAQ">
        <Container>
          <div className="row">
            {(title || body) && (
              <div className={textClasses}>
                {title && (
                  <h2 className="ParagraphFAQ__title">
                    {title}
                  </h2>
                )}
                {body && (
                  <WysiwygField
                    className="ParagraphFAQ__body"
                    property="schema:text"
                    html={body}
                  />
                )}
              </div>
            )}

            <div className={faqClasses}>
              <Accordion className="ParagraphFAQ__accordion">
                {faqItems.map((faqItem) => (
                  <ParagraphFAQAccordionItem faqItem={faqItem} />
                ))}
              </Accordion>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};
export default ParagraphFAQ;
